import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import PropTypes from "prop-types";
import { CalendarDays, List, Loader2, X } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Pagination } from "@/components/ui/pagination";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import PageHeader from "../PageHeader";
import usePageParam from "../../hooks/usePageParam";
import { trainingApi } from "../../services/api";
import { apiErrorMessage } from "../../services/apiError";
import HistoryCalendar from "./HistoryCalendar";
import HistoryRow from "./HistoryRow";
import { calendarSearch, historyView, monthOf, parseDay, parseMonth } from "./lib/calendar";
import { dayKey } from "./lib/format";
import { SESSION_PAGE_SIZE, pageRange } from "./lib/history";

const SUBTITLES = {
  list: "Every session, newest first. Open one to see its plan against what you did, fix a set, or change its feedback.",
  calendar:
    "Your sessions on the days they started, a month at a time. Choose a day to list its sessions; open one to see its plan against what you did, fix a set, or change its feedback.",
};

/**
 * The list: every session, newest first, a page of 20 at a time (the
 * API's page size) with the total shown. The page lives in the URL
 * (`?page=2`): only the newest request lands, and a page that no longer
 * exists (sessions discarded since) heals to page 1.
 */
function HistoryList({ timeZone, from = "" }) {
  const [page, setPage] = usePageParam();
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const request = useRef(0);

  useEffect(() => {
    const id = ++request.current;
    setLoading(true);
    setError(null);
    const load = async () => {
      try {
        const { data } = await trainingApi.sessions.list({ page, page_size: SESSION_PAGE_SIZE });
        if (id !== request.current) return;
        setRows(data?.results ?? []);
        setCount(data?.count ?? 0);
        setLoading(false);
      } catch (err) {
        if (id !== request.current) return;
        if (err?.response?.status === 404 && page > 1) {
          setPage(1, { replace: true }); // a page that isn't there any more
          return;
        }
        // An answer for another page must not show under this one.
        setRows([]);
        setCount(0);
        setError(apiErrorMessage(err, "Couldn't load your sessions."));
        setLoading(false);
      }
    };
    load();
    return () => {
      request.current += 1; // a late answer (or a left page) lands nowhere
    };
  }, [page, setPage, reloadTick]);

  const totalPages = Math.max(1, Math.ceil(count / SESSION_PAGE_SIZE));

  return (
    <>
      {error && (
        <Alert variant="destructive">
          <AlertDescription className="flex flex-wrap items-center gap-3">
            <span className="flex-1">{error}</span>
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-11 md:h-8"
              onClick={() => setReloadTick((tick) => tick + 1)}
            >
              Retry
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {loading && (
        <div className="py-12 text-center" role="status">
          <Loader2 className="inline size-6 animate-spin text-muted-foreground" aria-hidden="true" />
          <span className="sr-only">Loading your sessions…</span>
        </div>
      )}

      {!loading && !error && count === 0 && (
        <section aria-labelledby="training-history-empty" className="rounded-lg border bg-card p-4 md:p-6">
          <h2 id="training-history-empty" className="mt-0 mb-2 font-display text-xl font-semibold">
            No sessions yet
          </h2>
          <p className="mt-0 mb-4 text-muted-foreground">
            Your sessions show up here, newest first, once you start one on Today: check in, take
            the plan (or change it), and log your sets. Each one keeps its plan, its sets and your
            feedback — and you can fix any of them here afterwards.
          </p>
          <Button asChild className="h-11 w-full md:h-9 md:w-auto">
            <Link to="/training" data-empty-action>
              Go to Today
            </Link>
          </Button>
        </section>
      )}

      {!loading && rows.length > 0 && (
        <>
          <p className="m-0 text-sm text-foreground/80 tabular-nums">
            {count === 1
              ? "1 session."
              : totalPages > 1
                ? `Sessions ${pageRange(page, count)}, newest first.`
                : `${count} sessions, newest first.`}
          </p>
          <ol aria-label="Your sessions" data-history-list className="m-0 flex list-none flex-col gap-2 p-0">
            {rows.map((row) => (
              <HistoryRow key={row.id} row={row} timeZone={timeZone} from={from} />
            ))}
          </ol>
        </>
      )}

      {totalPages > 1 && (
        <Pagination
          page={page}
          total={totalPages}
          onChange={setPage}
          // The pager's own buttons are 36 px; on a phone they need 44.
          className="[&_button]:size-11 md:[&_button]:size-9"
        />
      )}
    </>
  );
}

HistoryList.propTypes = {
  timeZone: PropTypes.string,
  /** History's own query (`?page=2`), so a session can link back to it. */
  from: PropTypes.string,
};

/**
 * History (walkthroughs 7, 10), in two named modes — *Calendar* and
 * *List* — kept in the URL with the month and the chosen day
 * (`?view=calendar&month=2026-10&day=2026-10-06`), so Back steps through
 * them and a link reopens the same view. The list is the default (a bare
 * `/training/history`, or `?page=2`).
 *
 * Each session row: the day and time it started in the settings' time
 * zone, its template, its state (finished · "closed itself" when the
 * server closed it after 6 idle hours, D18 · open) and its set count; the
 * row opens the session. Choosing a mode, a month or *Today* is a history
 * entry; choosing a day replaces the entry, so Back from a session comes
 * back to that day. A discard on the session's page comes back here with
 * a notice in the navigation state, shown once.
 */
function TrainingHistory() {
  const { settings } = useOutletContext() || {};
  const timeZone = settings?.timezone;
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view = historyView(searchParams.get("view"));
  const now = new Date();
  const today = dayKey(now, timeZone);
  const thisMonth = monthOf(now, timeZone);
  const month = parseMonth(searchParams.get("month")) ?? thisMonth;
  const day = parseDay(searchParams.get("day"), month);
  // Said once: taken from the navigation state on arrival, which is then
  // cleared so a refresh doesn't say it again.
  const [notice, setNotice] = useState(() =>
    typeof location.state?.notice === "string" ? location.state.notice : null,
  );
  const pageRef = useRef(null);

  // The Dismiss button goes with the note: hand focus on to the first
  // session listed (or, with none, the way to Today), else to the mode
  // that's on — never <body>.
  const dismissNotice = () => {
    setNotice(null);
    setTimeout(() => {
      const page = pageRef.current;
      const target =
        page?.querySelector("[data-history-list] a, [data-empty-action]") ??
        page?.querySelector('[data-slot="toggle-group-item"][data-state="on"]');
      target?.focus();
    }, 0);
  };

  useEffect(() => {
    if (!location.state?.notice) return;
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
  }, [location.state, location.pathname, location.search, navigate]);

  // One URL write per choice (a second would build from the old params).
  const chooseView = (next) => {
    if (!next) return; // pressing the mode that's on would turn it off: it stays on
    setSearchParams(next === "calendar" ? calendarSearch({ month: thisMonth }) : {});
  };
  const showMonth = (next) => setSearchParams(calendarSearch({ month: next }));
  const chooseDay = (next) => setSearchParams(calendarSearch({ month, day: next }), { replace: true });

  return (
    <div ref={pageRef} className="page-container mt-6 pt-4 pb-8">
      <div className="mx-auto flex max-w-2xl flex-col gap-4">
        <PageHeader title="History" subtitle={SUBTITLES[view]} className="mb-2" />

        <ToggleGroup
          type="single"
          variant="outline"
          value={view}
          onValueChange={chooseView}
          aria-label="Show your sessions as"
          className="justify-start"
        >
          <ToggleGroupItem value="calendar" className="h-11 px-4 md:h-9">
            <CalendarDays aria-hidden="true" />
            Calendar
          </ToggleGroupItem>
          <ToggleGroupItem value="list" className="h-11 px-4 md:h-9">
            <List aria-hidden="true" />
            List
          </ToggleGroupItem>
        </ToggleGroup>

        {notice && (
          <Alert role="status">
            <AlertDescription className="flex items-start gap-2 text-foreground">
              <span className="flex-1">{notice}</span>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="-my-2 size-11 shrink-0 md:size-8"
                aria-label="Dismiss this note"
                title="Dismiss this note"
                onClick={dismissNotice}
              >
                <X aria-hidden="true" />
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {view === "calendar" ? (
          <HistoryCalendar
            timeZone={timeZone}
            month={month}
            day={day}
            today={today}
            from={location.search}
            onMonth={showMonth}
            onDay={chooseDay}
          />
        ) : (
          <HistoryList timeZone={timeZone} from={location.search} />
        )}
      </div>
    </div>
  );
}

export default TrainingHistory;
